@import '../../style/focus-outline.scss';
@import './com-nav-focus.scss';
@import '../../style/breakpoints.scss';
@import '../../style/reset.scss';

:host {
    --wcs-com-nav-menu-bar-height: calc(var(--wcs-semantic-size-base) * 8);
    --wcs-com-nav-border-color: var(--wcs-semantic-color-border-secondary);
    --wcs-com-nav-border-width: var(--wcs-semantic-border-width-default);
    --wcs-com-nav-background-color: var(--wcs-semantic-color-background-surface-primary);

    --wcs-com-nav-app-name-color: var(--wcs-semantic-color-text-primary);
    --wcs-com-nav-app-name-font-weight: var(--wcs-semantic-font-weight-heavy);
    --wcs-com-nav-app-name-font-size: var(--wcs-semantic-font-size-heading-5);
    --wcs-com-nav-app-name-line-height: var(--wcs-semantic-font-line-height-large);

    --wcs-com-nav-item-color: var(--wcs-semantic-color-foreground-action-neutral-default);
    --wcs-com-nav-item-font-weight: var(--wcs-semantic-font-weight-medium);

    --wcs-com-nav-focus-outline-color: var(--wcs-semantic-color-border-focus-base);


    // Spacings for navbar and mobile overlay
    --wcs-com-nav-vertical-padding: 0;
    --wcs-com-nav-horizontal-padding: var(--wcs-semantic-spacing-large);
    --wcs-com-nav-mobile-overlay-gap: var(--wcs-semantic-spacing-large);
    --wcs-com-nav-mobile-overlay-padding: var(--wcs-semantic-spacing-large);

    // Burger icon (mobile)
    --wcs-com-nav-mobile-menu-icon-border-color: var(--wcs-semantic-color-border-secondary);
    --wcs-com-nav-mobile-menu-icon-gap: calc(var(--wcs-semantic-spacing-base) * 2);
    --wcs-com-nav-mobile-menu-icon-margin-left: var(--wcs-semantic-spacing-base);
    --wcs-com-nav-mobile-menu-icon-color: var(--wcs-semantic-color-foreground-action-secondary-default);

    // Menu bar (desktop)
    --wcs-com-nav-menu-bar-gap: calc(var(--wcs-semantic-spacing-base) * 5); // 40px
    --wcs-com-nav-menu-bar-margin-left: calc(var(--wcs-semantic-spacing-base) * 6.25); // 50 px

    display: block;
    position: relative;
    height: var(--wcs-com-nav-menu-bar-height);
    border-bottom: var(--wcs-com-nav-border-width) solid var(--wcs-com-nav-border-color);

    .container {
        padding: var(--wcs-com-nav-vertical-padding) var(--wcs-com-nav-horizontal-padding);
        height: 100%;
        display: flex;
        align-items: center;
        flex-direction: row;
        justify-content: space-between;

        nav {
            display: flex;
            align-items: center;
        }
    }

    .container-left {
        display: flex;
        height: 100%;
        align-items: center;
        flex-direction: row;
    }

    .container-right {
        display: flex;
        align-items: center;
    }

    .app-name {
        font-weight: var(--wcs-com-nav-app-name-font-weight);
        font-size: var(--wcs-com-nav-app-name-font-size);
        line-height: var(--wcs-com-nav-app-name-line-height);
        color: var(--wcs-com-nav-app-name-color);

        ::slotted(a) {
            @include reset-anchor;
        }

        ::slotted(a:focus-visible) {
            outline: none;
        }
    }

    .app-name:focus-within {
        @include focus-outline($outline-color: var(--wcs-com-nav-focus-outline-color));
    }

    .menu-bar {
        display: none;
    }

    #mobile-menu-icon {
        display: flex;
        align-items: center;
        flex-wrap: nowrap;
        color: var(--wcs-com-nav-mobile-menu-icon-color);
        padding-left: var(--wcs-com-nav-mobile-menu-icon-gap);
        margin-left: var(--wcs-com-nav-mobile-menu-icon-margin-left);
        border-left: solid 1px var(--wcs-com-nav-mobile-menu-icon-border-color);
    }

    #mobile-menu-icon:focus {
        outline: none;
    }

    #mobile-menu-icon:focus-visible::after {
        @include focus-outline($outline-color: var(--wcs-com-nav-focus-outline-color));
    }

    @supports not selector(#mobile-menu-icon:focus-visible::after) {
        #mobile-menu-icon:focus::after {
            @include focus-outline($outline-color: var(--wcs-com-nav-focus-outline-color));
        }
    }

    button {
        background: none;
        border: none;
        padding: 0;
        margin: 0;
        font: inherit;
        color: inherit;
        cursor: pointer;
    }

    #mobile-menu-icon:after {
        font-family: icons;
        font-size: 18px;
        cursor: pointer;
        content: "\f198";
        line-height: 1;
        box-sizing: border-box;
    }

    #mobile-menu-icon[aria-expanded="true"]:after {
        content: "\f129";
    }

    .mobile-overlay {
        display: none;
    }

    .mobile-overlay[data-mobile-open] {
        padding: var(--wcs-com-nav-mobile-overlay-padding);
        position: absolute;
        top: var(--wcs-com-nav-menu-bar-height);
        left: 0;
        right: 0;
        display: flex;
        flex-direction: column;
        gap: var(--wcs-com-nav-mobile-overlay-gap);
        z-index: 9999;
        background-color: var(--wcs-com-nav-background-color);

        ::slotted(wcs-com-nav-item) {
            color: var(--wcs-com-nav-item-color);
            font-weight: var(--wcs-com-nav-item-font-weight);
        }
    }
}

@include for-tablet-portrait-up {
    :host {
        .container {
            margin: 0 auto;
            max-width: var(--wcs-com-content-max-width);
        }

        nav {
            height: 100%;
        }

        .menu-bar {
            display: flex;
            align-items: center;
            height: 100%;
            margin-left: var(--wcs-com-nav-menu-bar-margin-left);

            ::slotted(*:not(:first-child)) {
                margin-left: var(--wcs-com-nav-menu-bar-gap);
            }

            ::slotted(wcs-com-nav-item) {
                height: 100%;
                color: var(--wcs-com-nav-item-color);
                font-weight: var(--wcs-com-nav-item-font-weight);
            }

            // Focus style for nav item in menu bar
            ::slotted(wcs-com-nav-item:focus-within) {
                @include com-nav-item-desktop-focus-outline(var(--wcs-com-nav-focus-outline-color));
            }
        }

        #mobile-menu-icon {
            display: none;
        }

        .mobile-overlay {
            display: none !important;
        }
    }
}
